import config from 'config/config';
import React, { ChangeEvent, Fragment, FunctionComponent, useState, KeyboardEvent } from 'react';
import styles from './BlogContent.module.scss';
import { Block, BlockContent, BlockHeader, Icon, Typography } from 'components/elements';
import { CrossSmall as CrossSmallIcon } from 'assets/icons';
import { PostItems } from '..';
import { useIntl } from 'react-intl';
import { usePosts } from 'hooks';
import { useRouteTranslations } from 'utils';
import { WPAcfBasic, WPAcfGlobalBlogPost } from 'types/acf';
import { WPCategory } from 'types/categories';
import { WPSimpleArticle } from 'types/articles';
import { WPSimplePage } from 'types/pages';
import messages from 'messages/base-pl';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

interface OwnProps {
  page: WPSimplePage<WPAcfGlobalBlogPost>;
  categories?: WPCategory[];
}

type BlogContentProps = Omit<JSX.IntrinsicElements['div'], 'children'> & OwnProps;

const BlogContent: FunctionComponent<BlogContentProps> = props => {
  const POSTS_PER_PAGE = 9;
  const { page, categories } = props;

  const { search: urlCategoryParams = '', pathname } = useLocation();
  const history = useHistory();
  const selectedCategory = new URLSearchParams(urlCategoryParams);
  const { translateSlug } = useRouteTranslations();
  const { formatMessage } = useIntl();
  const [collection, setCollection] = useState(selectedCategory.get('category') || config.allCategories);
  const [searchInput, setSearchInput] = useState(selectedCategory.get('search') || '');
  const [search, setSearch] = useState(selectedCategory.get('search') || '');
  const [perPage, setPerPage] = useState(POSTS_PER_PAGE);
  const [blogItems, setBlogItems] = useState<WPSimpleArticle<WPAcfBasic>[]>([]);

  const { data: posts = [], loading: loadingPosts } = usePosts<WPSimpleArticle<WPAcfBasic>>({
    category_name: collection,
    per_page: perPage + 1,
    ...(search && { search }),
  });

  useEffect(() => {
    if (posts && !loadingPosts) {
      setBlogItems([...posts.slice(0, perPage)]);
    }
  }, [posts, perPage, loadingPosts]);

  useEffect(() => {
    setPerPage(POSTS_PER_PAGE);
  }, [collection, search]);

  const {
    acf: { blogPosts, message },
  } = page || {};

  const selectCollection = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event?.target?.value;
    const params = new URLSearchParams({ ...(value && { category: value }), ...(search && { search }) }).toString();
    if (value) {
      setCollection(value);
    } else {
      setCollection(config.allCategories);
    }
    history.replace(`${pathname}${params ? `?${params}` : ''}`);
  };

  const searchPosts = () => {
    const params = new URLSearchParams({
      ...(collection !== config.allCategories && { category: collection }),
      ...(searchInput && { search: searchInput }),
    }).toString();
    if (searchInput.length > 2 && search !== searchInput) {
      setSearch(searchInput);
    } else {
      setSearch('');
    }

    history.replace(`${pathname}${params ? `?${params}` : ''}`);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      searchPosts();
    }
  };

  const resetSearchField = () => {
    const params = new URLSearchParams({ ...(collection !== config.allCategories && { category: collection }) }).toString();

    setSearch('');
    setSearchInput('');
    history.replace(`${pathname}${params ? `?${params}` : ''}`);
  };

  const renderSelectField = (categories: WPCategory[]) => {
    const checkedOption = document.querySelector('#category-select option:checked');
    const checkedOptionSpan = document.querySelector('#category-selected-item span');
    const clientWidth = checkedOptionSpan ? `${checkedOptionSpan.clientWidth + 48}px` : 'auto';
    return (
      <>
        <Typography
          id="category-selected-item"
          mobile="new_neue_16"
          tablet="new_neue_16"
          desktop="new_neue_16"
          as="span"
          style={{ width: 0, overflow: 'hidden', position: 'relative' }}
        >
          <span style={{ position: 'absolute', width: 'max-content' }}>{checkedOption?.innerHTML}</span>
        </Typography>
        <select id="category-select" onChange={selectCollection} value={collection} style={{ width: clientWidth }}>
          <option value={''}>{formatMessage(messages.all_categories)}</option>
          {categories
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter(category => !config.specialBlogCategories.includes(category.slug as any))
            .map(({ slug, name }) => (
              <option key={`category-item-name-${slug}`} value={slug}>
                {name}
              </option>
            ))}
        </select>
      </>
    );
  };

  return (
    <Fragment>
      <Block>
        <BlockContent className={styles.headerWrapper} direction="row">
          <BlockHeader className={styles.header}>Blog</BlockHeader>
          <div className={styles.searchWrapper}>
            <input
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={formatMessage(messages.search)}
            />
            {search && (
              <button onClick={resetSearchField} className={styles.close}>
                <Icon size={20} icon={CrossSmallIcon} />
              </button>
            )}
          </div>
          <div className={styles.selectWrapper}>
            <Typography mobile="new_neue_16" tablet="new_neue_16" desktop="new_neue_16" as="span">
              {formatMessage(messages.articles)}
            </Typography>
            {categories && renderSelectField(categories)}
          </div>
        </BlockContent>
      </Block>
      {posts && (
        <PostItems
          mobile={1}
          tablet={3}
          desktop={3}
          category={translateSlug('blog')}
          posts={blogItems}
          linkLabel={blogPosts?.buttonLabel}
          showLink={true}
          showCategory
          loading={loadingPosts}
          loadMore={posts.length > perPage}
          length={perPage}
          onLoadMore={() => setPerPage(perPage + POSTS_PER_PAGE)}
        >
          {message && blogItems.length === 0 && !loadingPosts && (
            <Typography mobile="new_ibm_regular_16" tablet="new_ibm_regular_16" desktop="new_ibm_regular_16" as="span">
              {formatMessage(messages.no_results)}
            </Typography>
          )}
        </PostItems>
      )}
    </Fragment>
  );
};

export default BlogContent;
